/**
 * Brief stage (§5f, reworked) — the deliberate SETUP for a modelling session:
 * the MODELLING PERIOD (a whole running season, or a specific date range). The
 * session's name and question are set when it is created and edited with the
 * pen in the Open… list (Chris, 2026-10-28: they took "a pointless amount of
 * space" here; the name is on the stage bar all the time). Nothing is auto-picked; the planner chooses the scope
 * before anything is modelled. Provenance/calibration are shown so the
 * credibility gate is visible before any figure is quoted (MASTER_PLAN §6.4/§6.7).
 */
import type { ReactNode } from 'react'

export interface BriefScope {
  line: string
  /** Modes compared, each with the colour family it uses elsewhere. */
  modes: { label: string; kind: 'through' | 'grosmont' }[]
}

export interface BriefGovernance {
  owner: string
  dataAsAt: string
  baseline: { label: string; kind: 'info' | 'warn' }
  calibration: { label: string; kind: 'good' | 'warn' } | null
  /** When the season was last built (null = not built yet). */
  lastBuilt?: string | null
  /** The TTI uplift in force, in words (null = none). */
  tti?: string | null
}

export interface BriefStageProps {
  // ---- modelling period ----
  seasons: string[]
  season: string
  onSeasonChange: (s: string) => void
  periodMode: 'season' | 'range'
  onPeriodModeChange: (m: 'season' | 'range') => void
  rangeFrom: string
  rangeTo: string
  onRangeFromChange: (v: string) => void
  onRangeToChange: (v: string) => void

  // ---- where each month's data comes from ----
  /**
   * One row per month of the season being designed. `auto` is what the
   * automatic rule would pick, `chosen` is the planner's explicit choice (empty
   * string = leave it automatic), and `note` explains the pick in words.
   */
  monthSourceRows: {
    key: string
    label: string
    auto: string | null
    chosen: string
    note: string
    runDays: number | null
  }[]
  /** Every running season on record, newest first. */
  sourceSeasons: string[]
  onMonthSourceChange: (monthKey: string, season: string) => void
  /** Free-text note printed wherever these figures appear. */
  sourceReason: string
  onSourceReasonChange: (v: string) => void
  /** Persist the choices (and the rest of the model settings) as the default. */
  disruptedSave?: ReactNode

  scope: BriefScope
  governance: BriefGovernance
  /** The full calibration/backtest detail, re-homed here (MASTER_PLAN §4). */
  calibrationDetail?: ReactNode
  /** "Reset to railway defaults" card — composed by the orchestrator. */
  resetCard?: ReactNode
  /** The modelling settings — running mode, minimum profit per working and the
   *  Model settings drawer — moved here from the Modelling stage (Chris,
   *  2026-09-04: "they are lost on the build tab"). Composed by the orchestrator. */
  modelSettings?: ReactNode
  /** Which optimiser builds the timetable when you press Build. */
  engineMode: 'claude' | 'ilp'
  onEngineModeChange: (m: 'claude' | 'ilp') => void
}

export function BriefStage(p: BriefStageProps) {
  return (
    <div className="sh-grid2">
      {/* ---- scope + provenance: one slim strip (Chris, 2026-10-28) ---- */}
      <section className="sh-card sh-span2 sh-brief-strip">
        <span className="sh-brief-strip-item"><span className="sh-kv-key">Line</span> <strong>{p.scope.line}</strong></span>
        <span className="sh-brief-strip-item">
          <span className="sh-kv-key">Modes</span>{' '}
          {p.scope.modes.map((m) => (
            <span key={m.label} className={m.kind === 'through' ? 'sh-chip-green' : 'sh-chip-info'}>{m.label}</span>
          ))}
        </span>
        <span className="sh-brief-strip-item"><span className="sh-kv-key">Owner</span> <strong>{p.governance.owner}</strong></span>
        <span className="sh-brief-strip-item"><span className="sh-kv-key">Data as at</span> <strong>{p.governance.dataAsAt}</strong></span>
        <span className="sh-brief-strip-item">
          <span className="sh-kv-key">Baseline</span>{' '}
          <span className={p.governance.baseline.kind === 'info' ? 'sh-tag-info' : 'sh-prov sh-prov--assumed'}>{p.governance.baseline.label}</span>
        </span>
        <span className="sh-brief-strip-item">
          <span className="sh-kv-key">Model accuracy</span>{' '}
          {p.governance.calibration
            ? <span className={p.governance.calibration.kind === 'good' ? 'sh-tag-good' : 'sh-prov sh-prov--assumed'}>{p.governance.calibration.label}</span>
            : <span className="sh-prov sh-prov--assumed">not yet checked</span>}
        </span>
        {p.governance.tti && (
          <span className="sh-brief-strip-item"><span className="sh-kv-key">TTI</span> <span className="sh-prov sh-prov--assumed">{p.governance.tti}</span></span>
        )}
        <span className="sh-brief-strip-item">
          <span className="sh-kv-key">Model last built</span>{' '}
          {p.governance.lastBuilt
            ? <strong>{p.governance.lastBuilt}</strong>
            : <span className="sh-prov sh-prov--assumed">not built yet — ★ Build Model</span>}
        </span>
      </section>

      {/* ---- modelling engine (Chris, 2026-08-27): the optimiser that builds the
          timetable when you press Build. NYMR Claude is the proven default; the
          Advanced ILP engine is a global mixed-integer optimiser. ---- */}
      <section className="sh-card sh-card--pad sh-span2">
        <div className="sh-section-label">Modelling engine</div>
        <div className="sh-pats" style={{ marginTop: 8 }}>
          <button type="button" className={`sh-pat${p.engineMode === 'claude' ? ' sh-pat--active' : ''}`} onClick={() => p.onEngineModeChange('claude')}>NYMR Claude</button>
          <button type="button" className={`sh-pat${p.engineMode === 'ilp' ? ' sh-pat--active' : ''}`} onClick={() => p.onEngineModeChange('ilp')}>Advanced ILP Modelling</button>
        </div>
        <p className="stu-note sh-mt">
          {p.engineMode === 'claude'
            ? 'NYMR Claude — the default: a greedy constructor that grows each day one best marginal working at a time, then rescues returns and balances stock. Proven against the real railway.'
            : 'Advanced ILP Modelling — experimental: formulates the whole day as a mixed-integer program (candidate-trip selection + passenger flow) and solves it globally for contribution, then guarantees the return contract with the same rescue. May leave a residual unreturned where the peak is too tight to insert a return; NYMR Claude stays available and is often still better on the hard "everyone home" rule. Both are re-scored on identical arithmetic, so the two are directly comparable.'}
        </p>
      </section>

      {/* ---- modelling period ---- */}
      <section className="sh-card sh-card--pad sh-span2">
        <div className="sh-section-label">The season you are designing</div>
        <p className="stu-note" style={{ marginBottom: 12 }}>
          Every season you design is a speculation — it has no sales of its own. Its demand comes from the past
          seasons you choose month by month below; the <strong>Timetable</strong> stage sets which days it runs. This is
          just the label you are designing under, and the starting point it loads.
        </p>
        <div className="sh-period">
          <label className="sh-period-field">
            <span className="sh-period-lbl">Season to design</span>
            <select className="sh-period-select" value={p.season} onChange={(e) => p.onSeasonChange(e.target.value)}>
              {(p.seasons.length ? p.seasons : [p.season]).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <div className="sh-period-field">
            <span className="sh-period-lbl">Cover</span>
            <div className="sh-pats">
              <button type="button" className={`sh-pat${p.periodMode === 'season' ? ' sh-pat--active' : ''}`} onClick={() => p.onPeriodModeChange('season')}>Whole season</button>
              <button type="button" className={`sh-pat${p.periodMode === 'range' ? ' sh-pat--active' : ''}`} onClick={() => p.onPeriodModeChange('range')}>Specific dates</button>
            </div>
          </div>
          {p.periodMode === 'range' && (
            <>
              <label className="sh-period-field">
                <span className="sh-period-lbl">From</span>
                <input type="date" className="sh-period-date" value={p.rangeFrom} onChange={(e) => p.onRangeFromChange(e.target.value)} />
              </label>
              <label className="sh-period-field">
                <span className="sh-period-lbl">To</span>
                <input type="date" className="sh-period-date" value={p.rangeTo} onChange={(e) => p.onRangeToChange(e.target.value)} />
              </label>
            </>
          )}
        </div>
        <p className="stu-note sh-mt">
          {p.periodMode === 'season'
            ? 'The season roll-up counts every running day across the whole season, from the applied running set.'
            : 'The season roll-up counts only the running days that fall within these dates.'}
        </p>
      </section>

      {/* ---- where each month's data comes from -----------------------------
          Chris, 2026-08-25: "for each month give me a drop down which lists the
          running season I can take that months data from — that is the data,
          the sales, the capacity, the turns, the timetable you will use to
          generate data."

          This replaced an "excluded months" pill row that was incoherent the
          moment the season changed: the exclusions were stored as absolute
          months (2026-03), so with 2027/28 selected NO pill lit up and yet the
          panel still claimed three months were excluded. Explicit per-month
          choice removes the ambiguity — and Bridge 42 stops being a special
          concept and becomes three ordinary picks. */}
      <section className="sh-card sh-card--pad sh-span2">
        <div className="sh-section-label">Where each month&rsquo;s data comes from</div>
        <p className="stu-note">
          The season you are designing has not traded yet, so every month takes its
          <strong> sales, capacity, turns and timetable</strong> from a season that has.
          Leave a month on <em>automatic</em> and the model uses the most recent season that
          finished trading and describes the railway you intend to run; set it explicitly if
          you know better — a line closure, a washout, a year that could not reach part of
          the route.
        </p>
        <div className="sh-monthsrc">
          {p.monthSourceRows.map((m) => (
            <label className="sh-monthsrc-row" key={m.key}>
              <span className="sh-monthsrc-month">
                {m.label}
                {m.runDays != null && (
                  <span className="stu-note"> · {m.runDays} running day{m.runDays === 1 ? '' : 's'}</span>
                )}
              </span>
              <select
                className="sh-monthsrc-pick"
                value={m.chosen}
                onChange={(e) => p.onMonthSourceChange(m.key, e.target.value)}
                title={m.note}
              >
                <option value="">{m.auto ? `Automatic — ${m.auto}` : 'Automatic — nothing usable'}</option>
                {p.sourceSeasons.map((sn) => <option key={sn} value={sn}>{sn}</option>)}
              </select>
              <span className="sh-monthsrc-note stu-note">{m.note}</span>
            </label>
          ))}
        </div>
        <label className="sh-period-field sh-mt" style={{ display: 'block' }}>
          <span className="sh-period-lbl">A note on these choices (printed wherever their figures appear)</span>
          <input
            type="text" className="sh-brief-name"
            value={p.sourceReason}
            placeholder="e.g. Bridge 42 closed the line north of Goathland for the early 2026 season"
            onChange={(e) => p.onSourceReasonChange(e.target.value)}
          />
        </label>
        <p className="stu-note sh-mt">
          {p.monthSourceRows.filter((m) => m.chosen).length === 0
            ? 'Every month is on automatic. Hover a month to see why it picked the year it did.'
            : `${p.monthSourceRows.filter((m) => m.chosen).length} month${p.monthSourceRows.filter((m) => m.chosen).length === 1 ? '' : 's'} set by hand; the rest are automatic.`}
          {' '}A rebuild (★ Build Model) is needed for a change here to reach the figures.
        </p>
        {p.disruptedSave && <div className="sh-mt">{p.disruptedSave}</div>}
      </section>

      {p.modelSettings && <div className="sh-span2">{p.modelSettings}</div>}
      {p.calibrationDetail && <div className="sh-span2">{p.calibrationDetail}</div>}
      {p.resetCard && <div className="sh-span2">{p.resetCard}</div>}
    </div>
  )
}
